import { Button, Card, CardActions, CardContent, Chip, colors, Grid, Modal, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { ErrorMessage, EmptyState } from 'components';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Teacher, Lesson } from "services";
import useErrorCatcher from "utils/useErrorCatcher";

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    width: 700,
    maxHeight: '100%',
    overflowY: 'auto',
    maxWidth: '100%'
  },
  container: {
    marginTop: theme.spacing(3)
  },
  actions: {
    justifyContent: 'flex-end'
  },
  saveButton: {
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  },
  label: {
    fontSize: 18
  },
  section: {
    '& + &': {
      marginTop: theme.spacing(5)
    }
  },
  chip: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1)
  }
}));

let mounted = false;

const TeacherSet = props => {
  const { open, onClose, className, reload, ...rest } = props;

  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const runAsyncBlock = useErrorCatcher(error => setErrorMessage(error && error.message));
  const model = useSelector(state => state.models.lesson);
  const [teachers, setTeachers] = useState(model.teachers);
  const [options, setOptions] = useState([]);
  // const [selected, setSelected] = useState(null);

  useEffect(() => {
    mounted = true;
    runAsyncBlock(async () => {
      const { models } = await Teacher.list({ query: { limit: 9999 } });
      mounted && setOptions(models);
      return () => { mounted = false };
    }).finally(() => mounted && setLoading(false));
  }, []); // eslint-disable-line

  useEffect(() => {
    if (mounted) {
      setTeachers(model.teachers);
      setErrorMessage("");
    }
  }, [open]); // eslint-disable-line

  if (!open) {
    return null;
  }

  const handleClose = event => {
    event.preventDefault();

    mounted && setLoading(true);
    runAsyncBlock(async () => {
      await Lesson.set_teachers(model.id, { teachers: teachers.map(teacher => teacher.id) });
      mounted && setLoading(false);
      reload();
    }).finally(() => {
      onClose();
    });

  }

  const handleChange = event => {
    event.persist();
    let id = event.target.value
    if (!teachers.find(teacher => teacher.id === id)) {
      let teacher = options.find(teacher => teacher.id === parseInt(id))
      if (teacher) {
        mounted && setTeachers([...teachers, teacher]);
      }
    }
  };

  const deleteTeacher = (id) => {
    let newTeachers = [...teachers];
    let index = newTeachers.findIndex(teacher => teacher.id === id);
    if (index >= 0) {
      newTeachers.splice(index, 1);
      mounted && setTeachers(newTeachers);
    }
  }

  return (
    <Modal
      onClose={onClose}
      open={open}>
      <Card
        {...rest}
        className={clsx(classes.root, className)}>
        <form>
          <CardContent>
            <Typography align="center" gutterBottom variant="h3">
              Set Assigned Teachers
            </Typography>
            <div className={classes.section}>
              <Card>
                <CardContent className={classes.content}>
                  {teachers.map(teacher => (
                    <Chip
                      key={teacher.id}
                      className={classes.chip}
                      label={teacher.name_en}
                      onDelete={() => deleteTeacher(teacher.id)}
                    />
                  ))}
                  <EmptyState active={!teachers.length} message="No teachers assigned." />
                </CardContent>
              </Card>
            </div>
            <Grid className={classes.container} container spacing={3}>
              <Grid item md={12} xs={12}>
                <TextField
                  fullWidth
                  label="Add Teacher"
                  name="selected"
                  onChange={handleChange}
                  // eslint-disable-next-line react/jsx-sort-props
                  SelectProps={{ native: true }}
                  value={""}
                  variant="outlined"
                  InputLabelProps={{ shrink: true, classes: { root: classes.label } }}
                  select
                >
                  <option disabled value={""} label="Select Teacher" />
                  {options && options.length > 0 && options.map((opt, index) => (
                    <option key={index} value={opt.id} disabled={teachers.find(teacher => teacher.id === opt.id)} >{opt.name_en}</option>
                  ))}
                </TextField>
              </Grid>
              <ErrorMessage message={errorMessage} className={classes.error} />
              <Grid item />
            </Grid>
          </CardContent>
          <CardActions className={classes.actions}>
            <Button onClick={onClose} variant="contained">Close</Button>
            <Button className={classes.saveButton} onClick={handleClose} variant="contained" disabled={loading} >Save</Button>
          </CardActions>
        </form>
      </Card>
    </Modal >
  );
};

TeacherSet.propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func,
  open: PropTypes.bool
};

TeacherSet.defaultProps = {
  open: false,
  onClose: () => { }
};

export default TeacherSet;
